/**
 * OrderActionButton — the Trioty color-coded quick-action button (labeled).
 * Same tone system as the table/attention: red "Review payment", gray
 * confirm/prepare, blue mark-shipped/delivered; a bordered "View order" for
 * terminal states. Used by the mobile order cards so their actions match desktop.
 */

import type { OrderStatus, SellerOrderListItem } from "@/lib/api/types";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { useThemeStore } from "@/shared/theme/useTheme";
import { getStatusMeta } from "@/modules/orders/lib/order-status-meta";
import { actVisual, actColors, ActionGlyph } from "./order-visuals";

export function OrderActionButton({
  order,
  onQuickAction,
  onOpenQuickSheet,
  onSelect,
  busy = false,
}: {
  order: SellerOrderListItem;
  onQuickAction: (order: SellerOrderListItem, targetStatus: OrderStatus) => void;
  onOpenQuickSheet: (order: SellerOrderListItem) => void;
  onSelect: () => void;
  busy?: boolean;
}) {
  const t = useTranslations();
  const isDark = useThemeStore((s) => s.resolvedDark);
  const meta = getStatusMeta(order.status);
  const av = actVisual(order.status);

  const run = () => {
    const tr = meta.transition;
    if (!tr) return;
    if (tr.needsEta || tr.requiresDeclineReason) {
      onOpenQuickSheet(order);
    } else {
      onQuickAction(order, tr.targetStatus);
    }
  };

  if (meta.transition && av) {
    const c = actColors(av.tone, isDark);
    return (
      <button
        type="button"
        onClick={run}
        disabled={busy}
        className="inline-flex h-[40px] w-full items-center justify-center gap-2 rounded-[10px] text-[13px] font-bold transition-opacity hover:opacity-90 disabled:opacity-50"
        style={{ background: c.btnBg, color: c.btnFg }}
      >
        {busy ? (
          <svg aria-hidden="true" className="h-[15px] w-[15px] animate-spin" viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeDasharray="32" strokeDashoffset="12" />
          </svg>
        ) : (
          <>
            <ActionGlyph kind={av.icon} />
            {t(meta.nextLabelKey)}
          </>
        )}
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={onSelect}
      className="inline-flex h-[40px] w-full items-center justify-center gap-2 rounded-[10px] border border-[color:var(--border-strong)] bg-[color:var(--card)] text-[13px] font-bold text-[color:var(--text-secondary)] transition-colors hover:bg-[color:var(--soft)]"
    >
      {t(keys.orders.metaNextViewOrder)}
    </button>
  );
}
